import { CarIcon, ChevronRightIcon } from "lucide-react";
import { useSidePanelStore, type SidePanelContent } from "@/stores/side-panel-store";
import { useT } from "@/locale-context";
import type { VehicleDetailResult, VehicleSearchResult } from "@/lib/api";

export function SearchPlaceholder({
  data,
  toolCallId,
}: {
  data: VehicleSearchResult;
  toolCallId: string;
}) {
  const t = useT();
  const open = useSidePanelStore((s) => s.open);

  if (data.status !== "ok" || data.results.length === 0) {
    return (
      <div className="my-2 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm">
        <p className="font-semibold text-neutral-900 text-sm">
          {t("vehicleCard.noResults")}
        </p>
        {data.broadening.length > 0 ? (
          <ul className="mt-1.5 list-inside list-disc text-neutral-700 text-xs">
            {data.broadening.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  const content: SidePanelContent = { type: "search", data };

  return (
    <button
      type="button"
      onClick={() => open(content, toolCallId)}
      className="my-2 flex w-full items-center gap-3 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white px-4 py-3 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-blue-100">
        <CarIcon className="size-4 text-blue-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-neutral-900 text-sm">
          {t("sidePanel.foundVehicles", { count: data.count })}
        </p>
        <p className="text-blue-600 text-xs">{t("sidePanel.viewResults")}</p>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-blue-400" />
    </button>
  );
}

export function DetailPlaceholder({
  data,
  toolCallId,
}: {
  data: VehicleDetailResult;
  toolCallId: string;
}) {
  const t = useT();
  const open = useSidePanelStore((s) => s.open);

  if (data.status !== "ok" || !data.model) {
    return (
      <div className="my-2 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm">
        <p className="text-neutral-900 text-sm">{t("vehicleCard.notFound")}</p>
      </div>
    );
  }

  const model = data.model;
  const content: SidePanelContent = { type: "detail", data };

  return (
    <button
      type="button"
      onClick={() => open(content, toolCallId)}
      className="my-2 flex w-full items-center gap-3 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white px-3 py-2.5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
    >
      {model.marketingImages[0] ? (
        <img
          src={model.marketingImages[0]}
          alt={`${model.makeName} ${model.name}`}
          className="size-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-blue-100">
          <CarIcon className="size-5 text-blue-600" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-neutral-500 text-[10px] uppercase">{model.makeName}</p>
        <p className="font-semibold text-neutral-900 text-sm leading-tight">
          {model.name}
        </p>
        <p className="text-blue-600 text-xs">{t("sidePanel.viewDetails")}</p>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-blue-400" />
    </button>
  );
}
